import { useState, type FC } from "react";
import {
  Grid,
  GridColumn,
  type GridRowClickEvent,
  type GridSortChangeEvent,
  type GridFilterChangeEvent,
  type GridCellProps,
} from "@progress/kendo-react-grid";
import { orderBy, type SortDescriptor } from "@progress/kendo-data-query";
import React from "react";

export interface ColumnConfig {
  field: string;
  title?: string;
  headerCell?: React.ReactNode;
  width?: number | string;
  cell?: (props: GridCellProps) => any;
  headerClassName?: string;
  sticky?: boolean;
  className?: string;
}

interface KendoTableProps {
  data: any[];
  columns: ColumnConfig[];
  onRowClick?: (event: GridRowClickEvent) => void;
  pageable?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  showHeader?: boolean;
  pageSize?: number;
  className?: string;
  wrapperClassName?: string;
  scrollable?: boolean;
  height?: string | number;
  minTableWidth?: string | number;
  resizable?: boolean;
  hiddenColumns?: string[];
  stickyColumns?: string[];
}

const getHeightClass = (height: string | number) => {
  if (height === "auto") return "h-auto";
  if (typeof height === "number") return `h-[${height}px]`;
  return `h-[${height}]`;
};

const getMinWidthClass = (minTableWidth: string | number) => {
  if (minTableWidth === "auto") return "";
  if (typeof minTableWidth === "number") return `min-w-[${minTableWidth}px]`;
  return `min-w-[${minTableWidth}]`;
};

const KendoTable: FC<KendoTableProps> = ({
  data = [],
  columns = [],
  onRowClick,
  pageable = false,
  sortable = false,
  filterable = false,
  showHeader = true,
  pageSize = 10,
  className = "kendo-grid-transactions",
  wrapperClassName = "kendo-grid-custom",
  scrollable = true,
  height = "",
  minTableWidth = "auto",
  resizable = false,
  hiddenColumns = [],
  stickyColumns = [],
}) => {
  const [page, setPage] = useState<any>({ skip: 0, take: pageSize });
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [filter, setFilter] = useState<any>(null);

  const processedData = pageable
    ? orderBy(data, sort).slice(page.skip, page.skip + page.take)
    : orderBy(data, sort);

  const visibleColumns = columns.filter(
    (col) => !hiddenColumns.includes(col.field),
  );

  const createClickableCell = (
    originalCell?: (props: GridCellProps) => React.ReactNode,
    fieldName?: string, // Added to check if it's actions column
  ) => {
    const ClickableCell = (props: GridCellProps) => {
      const handleClick = (e: React.MouseEvent) => {
        // . CRITICAL FIX: Don't handle click for actions column
        if (fieldName === "actions") {
          return; // Let the ActionsCell handle its own clicks
        }

        e.preventDefault();
        e.stopPropagation();

        if (onRowClick) {
          const rowClickEvent: GridRowClickEvent = {
            dataItem: props.dataItem,
            syntheticEvent: e as any,
            nativeEvent: e.nativeEvent,
          } as GridRowClickEvent;
          onRowClick(rowClickEvent);
        }
      };

      const content = originalCell
        ? originalCell(props)
        : props.dataItem[props.field || ""];

      if (React.isValidElement(content) && content.type === "td") {
        const elementWithProps = content as React.ReactElement<
          React.TdHTMLAttributes<HTMLTableCellElement>
        >;

        // . CRITICAL FIX: Don't add click handler to actions column
        if (fieldName === "actions") {
          return elementWithProps; // Return as-is without wrapping
        }

        return React.cloneElement(elementWithProps, {
          onClick: handleClick,
          style: { ...elementWithProps.props.style, cursor: "pointer" },
        });
      }

      // . CRITICAL FIX: Don't add click handler to actions column
      if (fieldName === "actions") {
        return <td>{content}</td>; // No onClick, no cursor pointer
      }

      return (
        <td onClick={handleClick} style={{ cursor: "pointer" }}>
          {content}
        </td>
      );
    };

    ClickableCell.displayName = "ClickableCell";
    return ClickableCell;
  };

  const processedColumns = visibleColumns.map((col) => ({
    ...col,
    sticky: stickyColumns.includes(col.field) || col.sticky || false,
    // . Pass field name to createClickableCell so it knows which column it is
    cell: onRowClick ? createClickableCell(col.cell, col.field) : col.cell,
  }));

  const scrollableConfig = scrollable ? "scrollable" : "none";
  const heightClass = getHeightClass(height);
  const minWidthClass = getMinWidthClass(minTableWidth);

  if (!data.length && !columns.length) {
    return (
      <div className={wrapperClassName}>
        <div className="p-4 text-center text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div className={`${wrapperClassName} ${minWidthClass}`}>
      <Grid
        data={processedData}
        scrollable={scrollableConfig}
        resizable={resizable}
        sortable={sortable}
        sort={sort}
        onSortChange={
          sortable ? (e: GridSortChangeEvent) => setSort(e.sort) : undefined
        }
        filterable={filterable}
        filter={filter}
        onFilterChange={
          filterable
            ? (e: GridFilterChangeEvent) => setFilter(e.filter)
            : undefined
        }
        skip={pageable ? page.skip : undefined}
        take={pageable ? page.take : undefined}
        total={pageable ? data.length : undefined}
        pageable={pageable}
        onPageChange={pageable ? (e: any) => setPage(e.page) : undefined}
        navigatable={false}
        className={`kendo-grid-custom ${
          showHeader ? "" : "no-header"
        } ${className} ${heightClass}`}
      >
        {processedColumns.map((col: any, idx) => (
          <GridColumn
            key={idx}
            field={col.field}
            title={showHeader && col.headerCell ? col.headerCell : col.title}
            headerCell={col.headerCell}
            width={col.width}
            cells={col.cell ? { data: col.cell } : undefined}
            headerClassName={col.headerClassName}
            {...(col.sticky && { locked: true })}
          />
        ))}
      </Grid>
    </div>
  );
};

export default KendoTable;
